import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRightIcon, ClockIcon, EyeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { CalendarDays, RefreshCw } from 'lucide-react';
import BlogContentRenderer from '../components/blog/BlogContentRenderer';
import ReadingProgressBar from '../components/blog/ReadingProgressBar';
import BlogCard from '../components/blog/BlogCard';
import DakhlaButterflyLoader from '../components/common/DakhlaButterflyLoader';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import { API_ENDPOINTS, apiRequest } from '../config/api';

const getInitials = (name) => {
  if (!name) return 'DA';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);

  useDocumentMetadata(
    blog?.seo?.seo_title || (blog ? `${blog.title} - Dakhla Blog` : 'Blog - Dakhla'),
    blog?.seo?.seo_description || blog?.excerpt || '',
    blog?.seo?.seo_image_url || blog?.featured_image_url || ''
  );

  useEffect(() => {
    setLoading(true);
    setError(null);
    setBlog(null);
    setRelated([]);

    apiRequest(API_ENDPOINTS.BLOG_DETAIL(slug))
      .then((data) => {
        setBlog(data.blog);
        window.scrollTo(0, 0);
      })
      .catch((err) => {
        console.error('Failed to fetch blog post:', err);
        setError('Blog post not found.');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Related posts from the same category
  useEffect(() => {
    if (!blog?.category?.slug) return;
    apiRequest(`${API_ENDPOINTS.BLOGS}?category=${encodeURIComponent(blog.category.slug)}&limit=4`)
      .then((data) => setRelated((data.blogs || []).filter((b) => b.id !== blog.id).slice(0, 3)))
      .catch(() => setRelated([]));
  }, [blog]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center py-24">
        <DakhlaButterflyLoader text="Loading article..." />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center py-24">
        <div className="text-center bg-white rounded-2xl border border-slate-200 shadow-sm p-10 max-w-md">
          <p className="text-base font-bold text-slate-700">We couldn't find that article</p>
          <p className="mt-1.5 text-sm text-slate-400">It may have been unpublished or the link is out of date.</p>
          <Link to="/blog" className="mt-5 inline-flex items-center btn-primary text-sm cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2">
            <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
            Browse all articles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <ReadingProgressBar />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <nav aria-label="Breadcrumb" className="flex items-center text-xs sm:text-sm font-semibold text-slate-400 min-w-0">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">Home</Link>
            <ChevronRightIcon className="h-3.5 w-3.5 mx-1.5 shrink-0 text-slate-300" />
            <Link to="/blog" className="hover:text-primary-600 transition-colors whitespace-nowrap">Blog</Link>
            <ChevronRightIcon className="h-3.5 w-3.5 mx-1.5 shrink-0 text-slate-300" />
            <span className="text-slate-700 truncate min-w-0">{blog.title}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Article card */}
        <article className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Featured image */}
          {blog.featured_image_url && (
            <div className="relative aspect-[16/9] sm:aspect-[21/9] bg-slate-100">
              <img
                src={blog.featured_image_url}
                alt={blog.featured_image_alt || blog.title}
                className="w-full h-full object-cover"
              />
              {blog.category && (
                <span className="absolute top-4 left-4 px-3 py-1.5 bg-white/95 backdrop-blur-sm text-[#1E256F] text-xs font-bold rounded-lg shadow-sm border border-slate-200/80">
                  {blog.category.name}
                </span>
              )}
            </div>
          )}

          <div className="p-5 sm:p-8 lg:p-10">
            {!blog.featured_image_url && blog.category && (
              <span className="inline-block px-3 py-1 bg-slate-100 text-[#1E256F] text-xs font-bold rounded-lg border border-slate-200/80 mb-4">
                {blog.category.name}
              </span>
            )}

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-800 tracking-tight leading-tight">
              {blog.title}
            </h1>

            {blog.excerpt && (
              <p className="mt-4 text-base sm:text-lg text-slate-500 leading-relaxed font-medium">{blog.excerpt}</p>
            )}

            {/* Author + meta row */}
            <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-3">
              <div className="flex items-center min-w-0">
                {blog.author?.profile_picture_url ? (
                  <img src={blog.author.profile_picture_url} alt={blog.author_name || blog.author?.name || 'Author'} className="w-10 h-10 rounded-full object-cover border border-primary-100/30 shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary-50 border border-primary-100/30 text-primary-600 text-xs font-bold uppercase flex items-center justify-center shrink-0">
                    {getInitials(blog.author_name || blog.author?.name)}
                  </div>
                )}
                <div className="ml-3 min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{blog.author_name || blog.author?.name || 'Dakhla Team'}</p>
                  <p className="text-xs text-slate-400 font-medium">Author</p>
                </div>
              </div>
              <div className="flex items-center gap-5 text-xs font-semibold text-slate-400">
                <span className="flex items-center">
                  <CalendarDays className="h-3.5 w-3.5 mr-1.5" />
                  {formatDate(blog.published_at)}
                </span>
                <span className="flex items-center">
                  <ClockIcon className="h-3.5 w-3.5 mr-1.5" />
                  {blog.reading_time_minutes || 1} min read
                </span>
                {blog.view_count > 0 && (
                  <span className="flex items-center">
                    <EyeIcon className="h-3.5 w-3.5 mr-1.5" />
                    {blog.view_count.toLocaleString()} views
                  </span>
                )}
                {blog.updated_at && blog.published_at && (() => {
                  const updated = new Date(blog.updated_at);
                  const published = new Date(blog.published_at);
                  const diffDays = (updated - published) / (1000 * 60 * 60 * 24);
                  if (diffDays > 1) {
                    return (
                      <span className="flex items-center">
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        Updated {formatDate(blog.updated_at)}
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Content */}
            <div className="mt-8">
              <BlogContentRenderer blocks={blog.content} />
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mt-10 pt-6 border-t border-slate-100 flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mr-1">Tags:</span>
                {blog.tags.map((tag) => (
                  <Link
                    key={tag.id}
                    to={`/blog?search=${encodeURIComponent(tag.name)}`}
                    className="px-3 py-1 bg-slate-100 text-slate-700 hover:bg-[#1E256F]/10 hover:text-[#1E256F] text-xs font-semibold rounded-lg border border-slate-200 transition-colors"
                  >
                    #{tag.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </article>

        {/* Related posts */}
        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="text-xl font-bold text-slate-800 tracking-tight mb-4">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {related.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          </section>
        )}

        {/* Back link */}
        <div className="mt-10 text-center">
          <Link to="/blog" className="inline-flex items-center text-sm font-bold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-lg">
            <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
            Back to all articles
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
